import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchOrganisationById,
  updateOrganisation,
  changeOrgPlan,
  updateOrgSettings,
  addOrgAdmin,
  createAndAddOrgAdmin,
  removeOrgAdmin,
  addOrgDomain,
  removeOrgDomain,
  suspendOrganisation,
  reinstateOrganisation,
  clearCurrentOrganisation,
} from '../../store/organisationSlice';
import { membershipAPI, organisationAPI, organizationRoleAPI } from '../../api/services';
import { OrgPlan, OrgSettings } from '../../types';

type Tab = 'overview' | 'settings' | 'admins' | 'members' | 'domains' | 'database' | 'danger';

interface OrgMembership {
  _id: string;
  userId: { _id: string; name: string; email: string } | string;
  organizationRoleId?: { _id: string; name: string; code: string } | string | null;
  isPrimary: boolean;
  status: string;
}

interface RoleOption {
  _id: string;
  name: string;
  code: string;
}

interface UserSearchResult {
  _id: string;
  name: string;
  email: string;
}

const PLAN_COLORS: Record<OrgPlan, string> = {
  // free: 'bg-gray-100 text-gray-700',
  // starter: 'bg-blue-100 text-blue-700',
  // pro: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-yellow-100 text-yellow-700',
};

interface OrgDetailProps {
  orgId?: string;
  onClose?: () => void;
  isModal?: boolean;
}

const OrganisationDetail: React.FC<OrgDetailProps> = ({ orgId: propOrgId, onClose, isModal }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propOrgId || paramId;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentOrganisation: org, loading, error } = useAppSelector(
    (state) => state.organisations
  );

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Overview edit state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', contactEmail: '', billingEmail: '', website: '', timezone: '' });

  // Plan
  const [selectedPlan, setSelectedPlan] = useState<OrgPlan>('enterprise');

  // Settings
  const [settingsData, setSettingsData] = useState<Partial<OrgSettings>>({});

  // Admin management — search or create
  type AdminMode = 'search' | 'create';
  const [adminMode, setAdminMode] = useState<AdminMode>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ _id: string; name: string; email: string; phone?: string; orgId?: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [newAdminForm, setNewAdminForm] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '' });
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  // Domain management
  const [newDomain, setNewDomain] = useState('');
  const [newDomainPrimary, setNewDomainPrimary] = useState(false);

  // Suspend
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);

  // Generic confirm modal — replaces window.confirm
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void; destructive?: boolean } | null>(null);

  // Database tab
  type DbStatus = { hasOwnDatabase: boolean; databaseName?: string; databaseStatus?: string; databaseProvisionedAt?: string; databaseError?: string; connection: { state: string; host: string | null } };
  const [dbStatus, setDbStatus] = useState<DbStatus | null>(null);
  const [dbStatusLoading, setDbStatusLoading] = useState(false);
  const [dbProvisionForm, setDbProvisionForm] = useState({ mode: 'same' as 'same' | 'custom', databaseUri: '', databaseName: '' });
  const [dbProvisioning, setDbProvisioning] = useState(false);
  const [dbMigrating, setDbMigrating] = useState(false);
  const [dbMigrationStats, setDbMigrationStats] = useState<Record<string, { copied: number; skipped: number }> | null>(null);
  const [dbMsg, setDbMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showProvisionForm, setShowProvisionForm] = useState(false);

  // Members tab state
  const [orgMembers, setOrgMembers] = useState<OrgMembership[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberRoles, setMemberRoles] = useState<RoleOption[]>([]);
  const [memberUserSearch, setMemberUserSearch] = useState('');
  const [memberUserResults, setMemberUserResults] = useState<UserSearchResult[]>([]);
  const [memberUserSearchLoading, setMemberUserSearchLoading] = useState(false);
  const [memberUserShowDropdown, setMemberUserShowDropdown] = useState(false);
  const memberSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addMemberForm, setAddMemberForm] = useState({ userId: '', userName: '', organizationRoleId: '', isPrimary: false });
  const [addMemberErrors, setAddMemberErrors] = useState<{ userId?: string }>({});
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');

  useEffect(() => {
    if (id) dispatch(fetchOrganisationById(id));
    return () => { dispatch(clearCurrentOrganisation()); };
  }, [id, dispatch]);

  useEffect(() => {
    if (org) {
      setEditData({
        name: org.name,
        description: org.description || '',
        contactEmail: org.contactEmail || '',
        billingEmail: org.billingEmail || '',
        website: org.website || '',
        timezone: org.timezone,
      });
      setSelectedPlan(org.plan);
      setSettingsData({ ...org.settings });
    }
  }, [org]);

  const flash = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  // Fetch members when Members tab is opened
  const fetchOrgMembers = async (orgId: string) => {
    setMembersLoading(true);
    setMembersError('');
    try {
      const res = await membershipAPI.getOrgMembers(orgId);
      setOrgMembers(res.data.memberships || res.data || []);
    } catch (err: any) {
      setMembersError(err?.response?.data?.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleMembersTabOpen = () => {
    setActiveTab('members');
    if (org) fetchOrgMembers(org._id);
  };

  // Member user search
  const handleMemberUserSearch = (q: string) => {
    setMemberUserSearch(q);
    setMemberUserShowDropdown(true);
    if (memberSearchDebounce.current) clearTimeout(memberSearchDebounce.current);
    if (q.length < 2) { setMemberUserResults([]); return; }
    memberSearchDebounce.current = setTimeout(async () => {
      setMemberUserSearchLoading(true);
      try {
        const res = await organisationAPI.searchUsers(q);
        setMemberUserResults(res.data.users || []);
      } catch {
        setMemberUserResults([]);
      } finally {
        setMemberUserSearchLoading(false);
      }
    }, 300);
  };

  const handlePickMemberUser = (u: UserSearchResult) => {
    setAddMemberForm({ ...addMemberForm, userId: u._id, userName: u.name });
    setMemberUserSearch(u.name);
    setMemberUserShowDropdown(false);
    setAddMemberErrors({});
  };

  // Load roles for the target org when add member modal opens
  useEffect(() => {
    if (!showAddMemberModal || !org) return;
    organizationRoleAPI.getAllRoles(undefined, org._id).then((res) => {
      const roles = res.data.data ?? res.data.roles ?? (Array.isArray(res.data) ? res.data : []);
      setMemberRoles(roles);
    }).catch(() => {});
  }, [showAddMemberModal, org?._id]);

  const validateAddMember = () => {
    const errs: { userId?: string } = {};
    if (!addMemberForm.userId) errs.userId = 'Please select a user';
    setAddMemberErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddMemberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !validateAddMember()) return;
    setAddMemberLoading(true);
    setAddMemberError('');
    try {
      await membershipAPI.addUserToOrg({
        userId: addMemberForm.userId,
        orgId: org._id,
        organizationRoleId: addMemberForm.organizationRoleId || undefined,
        isPrimary: addMemberForm.isPrimary,
      });
      setShowAddMemberModal(false);
      setAddMemberForm({ userId: '', userName: '', organizationRoleId: '', isPrimary: false });
      setMemberUserSearch('');
      flash('success', 'Member added');
      fetchOrgMembers(org._id);
    } catch (err: any) {
      setAddMemberError(err?.response?.data?.message || 'Failed to add member');
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleRemoveMember = (membershipId: string) => {
    if (!org) return;
    setConfirmAction({
      title: 'Remove Member', message: 'Remove this member from the organisation?', destructive: true,
      onConfirm: async () => {
        try { await membershipAPI.removeMembership(membershipId); flash('success', 'Member removed'); fetchOrgMembers(org._id); }
        catch (err: any) { flash('error', err?.response?.data?.message || 'Failed to remove member'); }
      },
    });
  };

  const getMemberName = (m: OrgMembership) =>
    typeof m.userId === 'object' ? m.userId.name : m.userId;

  const getMemberEmail = (m: OrgMembership) =>
    typeof m.userId === 'object' ? m.userId.email : '';

  const getMemberRoleName = (m: OrgMembership) => {
    if (!m.organizationRoleId) return null;
    return typeof m.organizationRoleId === 'object' ? m.organizationRoleId.name : null;
  };

  const handleUpdateOverview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    try {
      await dispatch(updateOrganisation({ id: org._id, data: editData })).unwrap();
      setEditMode(false);
      flash('success', 'Organisation updated');
    } catch (err: any) {
      flash('error', err || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePlan = async () => {
    if (!org || selectedPlan === org.plan) return;
    setSaving(true);
    try {
      await dispatch(changeOrgPlan({ id: org._id, plan: selectedPlan })).unwrap();
      flash('success', `Plan changed to ${selectedPlan}`);
    } catch (err: any) {
      flash('error', err || 'Failed to change plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!org) return;
    setSaving(true);
    try {
      await dispatch(updateOrgSettings({ id: org._id, settings: settingsData })).unwrap();
      flash('success', 'Settings saved');
    } catch (err: any) {
      flash('error', err || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Search existing users
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setShowDropdown(true);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (q.length < 2) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await organisationAPI.searchUsers(q);
        setSearchResults(res.data.users || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handlePickUser = async (userId: string, userName: string) => {
    if (!org) return;
    setShowDropdown(false);
    setSearchQuery('');
    setSearchResults([]);
    setSaving(true);
    try {
      await dispatch(addOrgAdmin({ id: org._id, userId })).unwrap();
      await dispatch(fetchOrganisationById(org._id));
      flash('success', `${userName} added as admin`);
    } catch (err: any) {
      flash('error', err || 'Failed to add admin');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org) return;
    if (newAdminForm.password !== newAdminForm.confirmPassword) {
      flash('error', 'Passwords do not match');
      return;
    }
    if (newAdminForm.password.length < 8) {
      flash('error', 'Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    const createdName = newAdminForm.name.trim();
    try {
      await dispatch(createAndAddOrgAdmin({
        id: org._id,
        data: {
          name: createdName,
          email: newAdminForm.email.trim(),
          password: newAdminForm.password,
          phone: newAdminForm.phone.trim() || undefined,
        },
      })).unwrap();
      await dispatch(fetchOrganisationById(org._id));
      setNewAdminForm({ name: '', email: '', password: '', confirmPassword: '', phone: '' });
      setAdminMode('search');
      flash('success', `${createdName} created and added as admin`);
    } catch (err: any) {
      flash('error', err || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = (userId: string) => {
    if (!org) return;
    setConfirmAction({
      title: 'Remove Admin', message: 'Remove this admin from the organisation?', destructive: true,
      onConfirm: async () => {
        try { await dispatch(removeOrgAdmin({ id: org._id, userId })).unwrap(); await dispatch(fetchOrganisationById(org._id)); flash('success', 'Admin removed'); }
        catch (err: any) { flash('error', err || 'Failed to remove admin'); }
      },
    });
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !newDomain.trim()) return;
    setSaving(true);
    try {
      await dispatch(addOrgDomain({ id: org._id, domain: newDomain.trim(), isPrimary: newDomainPrimary })).unwrap();
      setNewDomain('');
      setNewDomainPrimary(false);
      flash('success', 'Domain added');
    } catch (err: any) {
      flash('error', err || 'Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDomain = (domain: string) => {
    if (!org) return;
    setConfirmAction({
      title: 'Remove Domain', message: `Remove domain "${domain}"?`, destructive: true,
      onConfirm: async () => {
        try { await dispatch(removeOrgDomain({ id: org._id, domain })).unwrap(); flash('success', 'Domain removed'); }
        catch (err: any) { flash('error', err || 'Failed to remove domain'); }
      },
    });
  };

  const handleSuspend = async () => {
    if (!org) return;
    try {
      await dispatch(suspendOrganisation({ id: org._id, reason: suspendReason })).unwrap();
      setShowSuspendConfirm(false);
      setSuspendReason('');
      flash('success', 'Organisation suspended');
    } catch (err: any) {
      flash('error', err || 'Failed to suspend');
    }
  };

  const handleReinstate = () => {
    if (!org) return;
    setConfirmAction({
      title: 'Reinstate Organisation', message: 'Reinstate this organisation? Users will regain access.',
      onConfirm: async () => {
        try { await dispatch(reinstateOrganisation(org._id)).unwrap(); flash('success', 'Organisation reinstated'); }
        catch (err: any) { flash('error', err || 'Failed to reinstate'); }
      },
    });
  };

  const fetchDbStatus = async (orgId: string) => {
    setDbStatusLoading(true);
    try {
      const res = await organisationAPI.getDatabaseStatus(orgId);
      setDbStatus(res.data);
    } catch {
      setDbStatus(null);
    } finally {
      setDbStatusLoading(false);
    }
  };

  const handleDatabaseTabOpen = () => {
    setActiveTab('database');
    if (org) fetchDbStatus(org._id);
  };

  const handleProvisionDatabase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org) return;
    setDbProvisioning(true);
    setDbMsg(null);
    try {
      const payload: { databaseUri?: string; databaseName?: string } = {};
      if (dbProvisionForm.mode === 'custom') {
        payload.databaseUri = dbProvisionForm.databaseUri.trim();
        if (dbProvisionForm.databaseName.trim()) payload.databaseName = dbProvisionForm.databaseName.trim();
      } else {
        payload.databaseName = (dbProvisionForm.databaseName.trim() || `org_${org.slug}`);
      }
      const res = await organisationAPI.provisionDatabase(org._id, payload as any);
      setDbMsg({ type: 'success', text: res.data.message || 'Database provisioned' });
      setShowProvisionForm(false);
      setDbProvisionForm({ mode: 'same', databaseUri: '', databaseName: '' });
      await fetchDbStatus(org._id);
      dispatch(fetchOrganisationById(org._id));
    } catch (err: any) {
      setDbMsg({ type: 'error', text: err?.response?.data?.message || err?.response?.data?.error || 'Provisioning failed' });
    } finally {
      setDbProvisioning(false);
    }
  };

  const handleMigrateToDatabase = () => {
    if (!org) return;
    setConfirmAction({
      title: 'Migrate Data', message: 'Copy all org data from the central database to the private database? Already-migrated documents will be skipped safely.',
      onConfirm: async () => {
        setDbMigrating(true); setDbMsg(null); setDbMigrationStats(null);
        try { const res = await organisationAPI.migrateToDatabase(org._id); setDbMsg({ type: 'success', text: res.data.message }); setDbMigrationStats(res.data.stats || null); }
        catch (err: any) { setDbMsg({ type: 'error', text: err?.response?.data?.message || 'Migration failed' }); }
        finally { setDbMigrating(false); }
      },
    });
  };

  const handleEvictConnection = (disable: boolean) => {
    if (!org) return;
    const confirmMsg = disable
      ? 'Revert to shared database? The org will use the central database until re-provisioned.'
      : 'Evict connection? It will reconnect automatically on the next request.';
    setConfirmAction({
      title: disable ? 'Revert to Shared DB' : 'Evict Connection', message: confirmMsg, destructive: disable,
      onConfirm: async () => {
        try { const res = await organisationAPI.evictDatabaseConnection(org._id, disable);
          setDbMsg({ type: 'success', text: res.data.message });
          await fetchDbStatus(org._id);
          if (disable) dispatch(fetchOrganisationById(org._id));
        } catch (err: any) {
          setDbMsg({ type: 'error', text: err?.response?.data?.message || 'Failed' });
        }
      },
    });
  };

  const SETTINGS_LABELS: { key: keyof OrgSettings; label: string; description: string }[] = [
    { key: 'requireProjectApproval', label: 'Require project approval', description: 'All projects need approval before going active' },
    { key: 'allowCrossDeptLinks', label: 'Allow cross-department links', description: 'Projects across departments can be linked' },
    { key: 'allowExternalReviewers', label: 'Allow external reviewers', description: 'External review links can be generated' },
    { key: 'enforceSprints', label: 'Enforce sprints', description: 'All work must be assigned to a sprint' },
    { key: 'taskWeightEnabled', label: 'Task weight & scoring', description: 'Show weight and performance score fields' },
    { key: 'bugTrackingEnabled', label: 'Bug tracking', description: 'Enable bug reports and management' },
    { key: 'knowledgeBaseEnabled', label: 'Knowledge base', description: 'Enable document and knowledge base features' },
    { key: 'timeTrackingEnabled', label: 'Time tracking', description: 'Enable time session logging on tasks' },
  ];

  if (loading && !org) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !org) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!org) return null;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'admins', label: `Admins (${org.adminUserIds.length})` },
    { id: 'members', label: `Members${orgMembers.length > 0 ? ` (${orgMembers.length})` : ''}` },
    { id: 'domains', label: `Domains (${org.domains.length})` },
    { id: 'database', label: 'Database' },
    { id: 'settings', label: 'Settings' },
    { id: 'danger', label: 'Danger Zone' },
  ];

  const contentBody = (
    <>
      {/* Flash message */}
      {msg && (
        <div className={`mx-5 mt-3 text-xs px-3 py-2 rounded-lg ${
          msg.type === 'success' ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'
        }`}>{msg.text}</div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 px-5">
        <div className="flex gap-0 -mb-px overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.id}
              onClick={() => {
                if (tab.id === 'members') handleMembersTabOpen();
                else if (tab.id === 'database') handleDatabaseTabOpen();
                else setActiveTab(tab.id);
              }}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Overview ────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="px-5 py-4 space-y-4">
          {/* Details */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500 font-medium">Details</span>
              {!editMode && <button onClick={() => setEditMode(true)} className="text-xs text-gray-400 hover:text-blue-600 transition-colors">Edit</button>}
            </div>

            {editMode ? (
              <form onSubmit={handleUpdateOverview} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Name</label>
                    <input required value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Timezone</label>
                    <input value={editData.timezone} onChange={e => setEditData({ ...editData, timezone: e.target.value })} placeholder="Africa/Nairobi"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Contact Email</label>
                    <input type="email" value={editData.contactEmail} onChange={e => setEditData({ ...editData, contactEmail: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Billing Email</label>
                    <input type="email" value={editData.billingEmail} onChange={e => setEditData({ ...editData, billingEmail: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Website</label>
                    <input value={editData.website} onChange={e => setEditData({ ...editData, website: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Description</label>
                    <textarea rows={2} value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none" />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setEditMode(false)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
                  <button type="submit" disabled={saving} className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3.5">
                {[
                  ['Contact Email', org.contactEmail || '—'],
                  ['Billing Email', org.billingEmail || '—'],
                  ['Website', org.website || '—'],
                  ['Timezone', org.timezone],
                  ['Billing Status', org.billingStatus],
                  ['Created', new Date(org.createdAt).toLocaleDateString()],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-28">{label}</span>
                    <span className="text-xs text-gray-700">{value}</span>
                  </div>
                ))}
                {org.description && (
                  <div className="col-span-2 flex items-start gap-3">
                    <span className="text-xs text-gray-400 w-28 shrink-0">Description</span>
                    <span className="text-xs text-gray-700">{org.description}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Plan & limits */}
          <div>
            <span className="text-xs text-gray-500 font-medium mb-3 block">Plan & Limits</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                ['Max Projects', org.limits.maxProjects],
                ['Max Users', org.limits.maxUsers],
                ['Max Departments', org.limits.maxDepartments],
                ['Storage (GB)', org.limits.storageGb],
              ].map(([label, val]) => (
                <div key={String(label)} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="text-lg font-bold text-gray-800">{val === -1 ? '∞' : val}</p>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Change plan</label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value as OrgPlan)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                >
                  {/* <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option> */}
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <button
                onClick={handleChangePlan}
                disabled={saving || selectedPlan === org.plan}
                className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Settings ────────────────────────────────────────────────────── */}
      {activeTab === 'settings' && (
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xs text-gray-500 font-medium">Feature Flags & Defaults</h2>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* Work mode + visibility defaults */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-100">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Default Work Mode</label>
              <select
                value={settingsData.defaultWorkMode || 'fluid'}
                onChange={(e) => setSettingsData({ ...settingsData, defaultWorkMode: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="fluid">Fluid — open, self-directed</option>
                <option value="strict">Strict — capacity-enforced sprints</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Default Project Visibility</label>
              <select
                value={settingsData.defaultProjectVisibility || 'private'}
                onChange={(e) => setSettingsData({ ...settingsData, defaultProjectVisibility: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="private">Private</option>
                <option value="public_org">Public within org</option>
              </select>
            </div>
          </div>

          {/* Boolean toggles */}
          <div className="space-y-4">
            {SETTINGS_LABELS.map(({ key, label, description }) => (
              <div key={key} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettingsData({ ...settingsData, [key]: !settingsData[key] })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    settingsData[key] ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ease-in-out ${
                      settingsData[key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Admins ──────────────────────────────────────────────────────── */}
      {activeTab === 'admins' && (
        <div className="px-5 py-4">
          <h2 className="font-semibold text-gray-900 mb-5">Admin Users</h2>

          {/* Mode toggle */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-5 w-fit">
            <button
              type="button"
              onClick={() => { setAdminMode('search'); setSearchQuery(''); setSearchResults([]); }}
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                adminMode === 'search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Find existing user
            </button>
            <button
              type="button"
              onClick={() => setAdminMode('create')}
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                adminMode === 'create' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create new user
            </button>
          </div>

          {/* ── Search mode ── */}
          {adminMode === 'search' && (
            <div className="relative mb-6">
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder="Search by name or email..."
                  className="flex-1 text-sm outline-none bg-transparent"
                />
                {searchLoading && (
                  <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                )}
              </div>

              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {searchResults.map((u) => {
                    const alreadyAdmin = org.adminUserIds.some(
                    (a) => (typeof a === 'string' ? a : a._id) === u._id
                  );
                    return (
                      <button
                        key={u._id}
                        type="button"
                        disabled={alreadyAdmin}
                        onMouseDown={() => handlePickUser(u._id, u.name)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${alreadyAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-700 text-xs font-semibold">
                            {u.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                        <div className="ml-auto flex-shrink-0 flex items-center gap-2">
                          {u.orgId && u.orgId !== org._id && (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Other org</span>
                          )}
                          {alreadyAdmin ? (
                            <span className="text-xs text-gray-400">Already admin</span>
                          ) : (
                            <span className="text-xs text-blue-600 font-medium">Add</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {showDropdown && searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                  <p className="text-sm text-gray-500">No users found for "{searchQuery}"</p>
                  <button
                    type="button"
                    onMouseDown={() => { setAdminMode('create'); setNewAdminForm({ ...newAdminForm, email: searchQuery.includes('@') ? searchQuery : '', name: '' }); }}
                    className="text-sm text-blue-600 hover:underline mt-1"
                  >
                    Create a new user instead →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Create mode ── */}
          {adminMode === 'create' && (
            <form onSubmit={handleCreateAdmin} className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-4">
              <p className="text-xs text-blue-600">
                A new user account will be created and added as admin. They'll be prompted to set a new password on first login.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                  <input
                    required
                    type="text"
                    value={newAdminForm.name}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                    placeholder="Jane Doe"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newAdminForm.phone}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, phone: e.target.value })}
                    placeholder="+254 700 000000"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  required
                  type="email"
                  value={newAdminForm.email}
                  onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                  placeholder="jane@acme.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      required
                      type={showAdminPassword ? 'text' : 'password'}
                      value={newAdminForm.password}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                      placeholder="Min. 8 chars"
                      className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
                    />
                    <button type="button" onClick={() => setShowAdminPassword(!showAdminPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showAdminPassword
                          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
                        }
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password <span className="text-red-500">*</span></label>
                  <input
                    required
                    type={showAdminPassword ? 'text' : 'password'}
                    value={newAdminForm.confirmPassword}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, confirmPassword: e.target.value })}
                    placeholder="Repeat"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white ${
                      newAdminForm.confirmPassword && newAdminForm.password !== newAdminForm.confirmPassword
                        ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  {newAdminForm.confirmPassword && newAdminForm.password !== newAdminForm.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setAdminMode('search')} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors font-medium">
                  {saving ? 'Creating...' : 'Create & Add as Admin'}
                </button>
              </div>
            </form>
          )}

          {/* Admin list */}
          {org.adminUserIds.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No admins yet</p>
          ) : (
            <ul className="space-y-2">
              {org.adminUserIds.map((entry) => {
                const uid = typeof entry === 'string' ? entry : entry._id;
                const name = typeof entry === 'object' ? entry.name : null;
                const email = typeof entry === 'object' ? entry.email : null;
                const initials = name
                  ? name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
                  : null;
                const isCreator = uid === org.createdBy;

                return (
                  <li key={uid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        {initials ? (
                          <span className="text-blue-700 text-xs font-semibold">{initials}</span>
                        ) : (
                          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0">
                        {name ? (
                          <>
                            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                            <p className="text-xs text-gray-500 truncate">{email}</p>
                          </>
                        ) : (
                          <span className="text-sm text-gray-700 font-mono">{uid}</span>
                        )}
                      </div>
                      {isCreator && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex-shrink-0">Creator</span>
                      )}
                    </div>
                    {!isCreator && (
                      <button onClick={() => handleRemoveAdmin(uid)} className="text-xs text-red-500 hover:text-red-700 flex-shrink-0 ml-3">
                        Remove
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* ── Members ─────────────────────────────────────────────────────── */}
      {activeTab === 'members' && (
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xs text-gray-500 font-medium">Members</h2>
            <button
              onClick={() => { setAddMemberForm({ userId: '', userName: '', organizationRoleId: '', isPrimary: false }); setAddMemberErrors({}); setAddMemberError(''); setMemberUserSearch(''); setMemberUserResults([]); setShowAddMemberModal(true); }}
              className="text-xs text-gray-400 hover:text-blue-600 transition-colors"
            >
              + Add Member
            </button>
          </div>

          {membersLoading ? (
            <div className="flex justify-center py-12">
              <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : membersError ? (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{membersError}</div>
          ) : orgMembers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No members yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">Name</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">Email</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">Role</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">Primary</th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4">Status</th>
                    <th className="text-right text-xs font-medium text-gray-500 pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orgMembers.map((m) => {
                    const name = getMemberName(m);
                    const email = getMemberEmail(m);
                    const roleName = getMemberRoleName(m);
                    return (
                      <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-blue-700 text-xs font-semibold">
                                {name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900 truncate max-w-[120px]">{name}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-gray-500 truncate max-w-[160px]">{email || '—'}</td>
                        <td className="py-3 pr-4 text-gray-600">{roleName || '—'}</td>
                        <td className="py-3 pr-4">
                          {m.isPrimary ? (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Primary</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {m.status}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => handleRemoveMember(m._id)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Add Member Modal */}
          {showAddMemberModal && (
            <div className="!mt-0 fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowAddMemberModal(false)}>
              <div className="absolute inset-0 bg-black/30" />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-900">Add Member</span>
                  <button onClick={() => setShowAddMemberModal(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <form onSubmit={handleAddMemberSubmit}>
                <div className="px-5 py-4 space-y-3">

                {addMemberError && (
                  <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{addMemberError}</div>
                )}

                <div className="space-y-3">
                  {/* User search */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      User <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white ${
                        addMemberErrors.userId ? 'border-red-400' : 'border-gray-300'
                      }`}>
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={memberUserSearch}
                          onChange={(e) => handleMemberUserSearch(e.target.value)}
                          onFocus={() => memberUserSearch.length >= 2 && setMemberUserShowDropdown(true)}
                          onBlur={() => setTimeout(() => setMemberUserShowDropdown(false), 150)}
                          placeholder="Search by name or email..."
                          className="flex-1 text-sm outline-none bg-transparent"
                        />
                        {memberUserSearchLoading && (
                          <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                        )}
                      </div>

                      {memberUserShowDropdown && memberUserResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                          {memberUserResults.map((u) => (
                            <button
                              key={u._id}
                              type="button"
                              onMouseDown={() => handlePickMemberUser(u)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                            >
                              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-blue-700 text-xs font-semibold">
                                  {u.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                                <p className="text-xs text-gray-500 truncate">{u.email}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {memberUserShowDropdown && memberUserSearch.length >= 2 && !memberUserSearchLoading && memberUserResults.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                          <p className="text-sm text-gray-500">No users found for "{memberUserSearch}"</p>
                        </div>
                      )}
                    </div>
                    {addMemberErrors.userId && (
                      <p className="text-xs text-red-500 mt-1">{addMemberErrors.userId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Role (optional)</label>
                    <select
                      value={addMemberForm.organizationRoleId}
                      onChange={(e) => setAddMemberForm({ ...addMemberForm, organizationRoleId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                    >
                      <option value="">No role assigned</option>
                      {memberRoles.map((r) => (
                        <option key={r._id} value={r._id}>{r.name}</option>
                      ))}
                    </select>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addMemberForm.isPrimary}
                      onChange={(e) => setAddMemberForm({ ...addMemberForm, isPrimary: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-xs text-gray-600">Set as primary organisation</span>
                  </label>
                </div>{/* end space-y-3 */}
                </div>{/* end px-5 py-4 */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
                  <button type="button" onClick={() => setShowAddMemberModal(false)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
                  <button type="submit" disabled={addMemberLoading}
                    className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                    {addMemberLoading ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Domains ─────────────────────────────────────────────────────── */}
      {activeTab === 'domains' && (
        <div className="px-5 py-4">
          <h2 className="font-semibold text-gray-900 mb-5">Domains</h2>

          <form onSubmit={handleAddDomain} className="flex gap-3 mb-6 flex-wrap">
            <input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="acme.com"
              className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={newDomainPrimary}
                onChange={(e) => setNewDomainPrimary(e.target.checked)}
                className="rounded"
              />
              Primary
            </label>
            <button
              type="submit"
              disabled={saving || !newDomain.trim()}
              className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              Add Domain
            </button>
          </form>

          {org.domains.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No domains registered</p>
          ) : (
            <ul className="space-y-2">
              {org.domains.map((d) => (
                <li key={d.domain} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="text-sm font-mono text-gray-800">{d.domain}</span>
                    {d.isPrimary && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Primary</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${d.verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {d.verified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(d.domain)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Database ────────────────────────────────────────────────────── */}
      {activeTab === 'database' && (
        <div className="space-y-5">
          {dbMsg && (
            <div className={`p-3 rounded-lg text-sm border ${
              dbMsg.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {dbMsg.text}
            </div>
          )}

          {/* Status card */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs text-gray-500 font-medium">Database Status</h2>
              <button
                onClick={() => org && fetchDbStatus(org._id)}
                className="text-xs text-blue-600 hover:underline"
              >
                Refresh
              </button>
            </div>

            {dbStatusLoading ? (
              <div className="flex justify-center py-6">
                <svg className="animate-spin w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : dbStatus ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Mode</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {dbStatus.hasOwnDatabase ? 'Private DB' : 'Shared DB'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      dbStatus.databaseStatus === 'ready' ? 'bg-green-100 text-green-700' :
                      dbStatus.databaseStatus === 'provisioning' ? 'bg-yellow-100 text-yellow-700' :
                      dbStatus.databaseStatus === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        dbStatus.databaseStatus === 'ready' ? 'bg-green-500' :
                        dbStatus.databaseStatus === 'provisioning' ? 'bg-yellow-500' :
                        dbStatus.databaseStatus === 'error' ? 'bg-red-500' :
                        'bg-gray-400'
                      }`} />
                      {dbStatus.databaseStatus ?? 'not set'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Connection</p>
                    <span className={`text-xs font-medium ${
                      dbStatus.connection.state === 'connected' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {dbStatus.connection.state}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">DB Name</p>
                    <p className="text-xs font-mono text-gray-700 truncate">{dbStatus.databaseName ?? '—'}</p>
                  </div>
                </div>

                {dbStatus.databaseStatus === 'ready' && dbStatus.connection.host && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                    <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Connected to <span className="font-mono text-gray-700 ml-1">{dbStatus.connection.host}</span>
                    {dbStatus.databaseProvisionedAt && (
                      <span className="ml-auto">
                        Provisioned {new Date(dbStatus.databaseProvisionedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}

                {dbStatus.databaseStatus === 'error' && dbStatus.databaseError && (
                  <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{dbStatus.databaseError}</span>
                  </div>
                )}

                {dbStatus.hasOwnDatabase && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {dbStatus.databaseStatus === 'ready' && (
                      <button
                        onClick={handleMigrateToDatabase}
                        disabled={dbMigrating}
                        className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors flex items-center gap-1.5"
                      >
                        {dbMigrating ? (
                          <>
                            <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Migrating...
                          </>
                        ) : 'Migrate data'}
                      </button>
                    )}
                    <button
                      onClick={() => { setShowProvisionForm(true); setDbMsg(null); }}
                      className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {dbStatus.databaseStatus === 'error' ? 'Retry provisioning' : 'Re-provision'}
                    </button>
                    <button
                      onClick={() => handleEvictConnection(false)}
                      className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Evict connection
                    </button>
                    <button
                      onClick={() => handleEvictConnection(true)}
                      className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      Revert to shared DB
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Could not load database status</p>
            )}
          </div>

          {/* Migration stats */}
          {dbMigrationStats && (
            <div className="px-5 py-4">
              <h2 className="font-semibold text-gray-900 mb-4">Migration Results</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left pb-2 text-gray-500 font-medium pr-6">Collection</th>
                      <th className="text-right pb-2 text-gray-500 font-medium pr-4">Copied</th>
                      <th className="text-right pb-2 text-gray-500 font-medium">Skipped (existed)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {Object.entries(dbMigrationStats)
                      .sort(([, a], [, b]) => b.copied - a.copied)
                      .map(([name, s]) => (
                        <tr key={name} className="hover:bg-gray-50">
                          <td className="py-1.5 pr-6 font-mono text-gray-700">{name}</td>
                          <td className="py-1.5 pr-4 text-right font-semibold text-green-700">{s.copied}</td>
                          <td className={`py-1.5 text-right ${s.skipped > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {s.skipped < 0 ? 'error' : s.skipped}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Provision form */}
          {(!dbStatus?.hasOwnDatabase || showProvisionForm) && (
            <div className="px-5 py-4">
              <h2 className="font-semibold text-gray-900 mb-1">
                {dbStatus?.hasOwnDatabase ? 'Re-provision Database' : 'Provision Private Database'}
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Connect a dedicated MongoDB database for this organisation. All future reads and writes
                for this org will be routed to this database. Orgs without provisioning continue to use
                the shared central database unchanged.
              </p>
              <form onSubmit={handleProvisionDatabase} className="space-y-4">
                {/* Mode toggle */}
                <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
                  <button
                    type="button"
                    onClick={() => setDbProvisionForm({ ...dbProvisionForm, mode: 'same' })}
                    className={`px-4 py-1.5 text-xs rounded-md font-medium transition-colors ${
                      dbProvisionForm.mode === 'same' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Same MongoDB server
                  </button>
                  <button
                    type="button"
                    onClick={() => setDbProvisionForm({ ...dbProvisionForm, mode: 'custom' })}
                    className={`px-4 py-1.5 text-xs rounded-md font-medium transition-colors ${
                      dbProvisionForm.mode === 'custom' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Custom URI
                  </button>
                </div>

                {dbProvisionForm.mode === 'same' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Database Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      value={dbProvisionForm.databaseName}
                      onChange={(e) => setDbProvisionForm({ ...dbProvisionForm, databaseName: e.target.value })}
                      placeholder={`org_${org.slug}`}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      A new database will be created on the server's MongoDB instance. Leave blank to use <span className="font-mono">org_{org.slug}</span>.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        MongoDB Connection URI <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        type="text"
                        value={dbProvisionForm.databaseUri}
                        onChange={(e) => setDbProvisionForm({ ...dbProvisionForm, databaseUri: e.target.value })}
                        placeholder="mongodb+srv://user:pass@cluster.mongodb.net/dbname"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-gray-300"
                      />
                      <p className="text-xs text-gray-400 mt-1">Stored encrypted, never returned in API responses.</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Database Name (optional)</label>
                      <input
                        type="text"
                        value={dbProvisionForm.databaseName}
                        onChange={(e) => setDbProvisionForm({ ...dbProvisionForm, databaseName: e.target.value })}
                        placeholder={`org_${org.slug}`}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
                      />
                    </div>
                  </>
                )}
                <div className="flex gap-3">
                  {showProvisionForm && (
                    <button
                      type="button"
                      onClick={() => setShowProvisionForm(false)}
                      className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={dbProvisioning}
                    className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors font-medium"
                  >
                    {dbProvisioning ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Provisioning...
                      </span>
                    ) : 'Provision Database'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ── Danger Zone ─────────────────────────────────────────────────── */}
      {activeTab === 'danger' && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <h2 className="font-semibold text-red-700 mb-5">Danger Zone</h2>

          <div className="space-y-4">
            {org.isActive ? (
              <div className="flex items-start justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Suspend organisation</p>
                  <p className="text-xs text-gray-500 mt-0.5">Prevents all users from accessing this org's resources</p>
                </div>
                <button
                  onClick={() => setShowSuspendConfirm(true)}
                  className="ml-4 flex-shrink-0 text-xs bg-red-50 text-red-700 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-100 font-medium transition-colors"
                >
                  Suspend
                </button>
              </div>
            ) : (
              <div className="flex items-start justify-between p-4 border border-green-200 rounded-lg bg-green-50">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Reinstate organisation</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {org.suspendedReason ? `Suspended: ${org.suspendedReason}` : 'Restore access for all users'}
                  </p>
                </div>
                <button
                  onClick={handleReinstate}
                  className="ml-4 flex-shrink-0 text-xs bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium transition-colors"
                >
                  Reinstate
                </button>
              </div>
            )}
          </div>

          {/* Suspend confirm */}
          {showSuspendConfirm && (
            <div className="!mt-0 fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowSuspendConfirm(false)}>
              <div className="absolute inset-0 bg-black/30" />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-900">Suspend Organisation</span>
                  <button onClick={() => setShowSuspendConfirm(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <p className="text-sm text-gray-600">All users in <strong>{org.name}</strong> will lose access immediately.</p>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Reason (optional)</label>
                    <textarea rows={2} value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
                      placeholder="Payment overdue, policy violation..."
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none" />
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
                  <button onClick={() => setShowSuspendConfirm(false)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
                  <button onClick={handleSuspend} className="text-xs bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium transition-colors">Suspend</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Generic Confirm Modal ── */}
      {confirmAction && (
        <div className="!mt-0 fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmAction(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">{confirmAction.title}</span>
              <button onClick={() => setConfirmAction(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">{confirmAction.message}</p>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
              <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
              <button onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}
                className={`text-xs px-4 py-2 rounded-lg font-medium transition-colors ${
                  confirmAction.destructive ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Modal mode — render inside 90vw×90vh modal shell
  if (isModal && onClose) {
    return (
      <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-200 shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-2.5 text-xs">
              {org ? (
                <>
                  {org.logoUrl ? (
                    <img src={org.logoUrl} alt={org.name} className="w-7 h-7 rounded-lg object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 font-bold text-[10px]">{org.name.slice(0, 2).toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-sm font-semibold text-gray-900">{org.name}</span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[org.plan]}`}>{org.plan}</span>
                  <span className="text-gray-300">·</span>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${org.isActive ? 'bg-green-500' : 'bg-red-400'}`} />
                    <span className="text-gray-400">{org.isActive ? 'Active' : 'Suspended'}</span>
                  </div>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-400 font-mono">{org.slug}</span>
                </>
              ) : (
                <span className="text-gray-400">Loading...</span>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {contentBody}
          </div>
        </div>
      </div>
    );
  }

  // Page mode — original full-page layout
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <button onClick={() => navigate('/admin/organisations')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-6">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Organisations
      </button>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
          {org?.logoUrl ? <img src={org.logoUrl} alt={org.name} className="w-14 h-14 rounded-xl object-cover" /> : <span className="text-blue-700 font-bold text-xl">{org?.name.slice(0, 2).toUpperCase()}</span>}
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{org?.name}</h1>
            {org && <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${PLAN_COLORS[org.plan]}`}>{org.plan}</span>}
          </div>
          <p className="text-sm text-gray-500 font-mono">{org?.slug}</p>
        </div>
      </div>
      {contentBody}
    </div>
  );
};

export default OrganisationDetail;
